// O cartão do site (desenho da tela de 27/10/2026, §2): o MESMO nos 4 lugares (bolha do chat e do atendimento, post,
// comentário), em quatro peles (`propria`, `propria-portal`, `dos-outros`, `feed`) e duas formas (`faixa`, com a
// capa 1,91:1 no topo; `compacta`, com a miniatura de 56 px ou sem imagem). Quem decide SE há cartão é o
// `CartaoDoLink`; este só desenha o que recebe.
//
// O cartão inteiro é o link (nada de botão dentro de link): fora, outra aba e sem referrer; da casa, `<Link>` do
// router (ou `<a href>` se não há Router: as suítes antigas da bolha renderizam sem ele).
//
// 🔴 A cicatriz do `line-clamp` (medida no Chrome em 21/10): o `display` de `block`, `flex` ou `grid` na MESMA
// className vence o `-webkit-box` do `line-clamp-*` e o corte não acontece. Por isso a descrição da faixa é só
// `line-clamp-2` (a da compacta é `block truncate`, uma linha), o título é só `line-clamp-2`, e o domínio é um
// `flex` com um `<span class="truncate">` dentro (reticência não pega em filho anônimo de flex).
// 🔴 Título e descrição levam `dir="auto"`: o servidor não tira U+202E nem U+2066-2069, e um título hostil
// inverteria o resto da linha.
// 🔴 O cartão DESMONTA quando deve sumir (quem chama devolve `null`), nunca `hidden`: o `useEntradaSemPulo` mede o
// cartão e somaria a altura inteira ao rolar.
import { Globe } from "lucide-react";
import { useRef, useState, type MouseEvent } from "react";
import { Link, useInRouterContext } from "react-router-dom";

import { useEntradaSemPulo } from "@/hooks/useEntradaSemPulo";
import {
  caminhoRelativoSeguro,
  ehHttpOuHttps,
  formaDoCartao,
  type AparenciaDoCartao,
  type PreviaDoCartao,
} from "@/lib/links/previaDoCartao";
import { cn } from "@/lib/utils";

interface Props {
  previa: PreviaDoCartao;
  aparencia: AparenciaDoCartao;
  /** `em-cima`: o cartão antes do texto da bolha (`mb-1.5`); `embaixo`: depois do texto do post (`mt-2`). */
  posicao: "em-cima" | "embaixo";
  /** Da mensagem, do post ou do comentário: vira o `data-testid`. */
  id: string;
  /** O comentário do feed: miniatura mesmo com uma capa grande. */
  forcarCompacta?: boolean;
}

const BASE =
  "block w-[22rem] max-w-full select-none overflow-hidden rounded border text-left text-xs leading-4 no-underline transition-colors focus-visible:outline-none focus-visible:ring-2";

const POR_APARENCIA: Record<AparenciaDoCartao, string> = {
  propria: "border-white/20 bg-black/15 text-white hover:bg-black/20 focus-visible:ring-white",
  "propria-portal":
    "border-white/20 bg-black/25 text-white hover:bg-black/30 focus-visible:ring-white",
  "dos-outros":
    "border-border/70 bg-foreground/5 text-foreground hover:bg-foreground/[0.08] focus-visible:ring-ring",
  feed: "w-[28rem] rounded-lg border-border bg-muted/30 text-foreground hover:bg-muted/50 focus-visible:ring-ring",
};

const NAO_SOBE_O_CLIQUE = (e: MouseEvent) => e.stopPropagation();

/**
 * Só http e https viram <a>: qualquer outro endereço (`javascript:`, `data:`) não desenha cartão nenhum. A guarda
 * fica num componente de fora para o de dentro, que tem os hooks, nunca rodar com uma raiz que não existe.
 */
export function CartaoDeLink(props: Props) {
  if (!ehHttpOuHttps(props.previa.url)) return null;
  return <CartaoSeguro {...props} />;
}

function CartaoSeguro({ previa, aparencia, posicao, id, forcarCompacta }: Props) {
  const dentroDoRouter = useInRouterContext();
  const raizRef = useRef<HTMLAnchorElement>(null);
  // Guarda QUAL imagem falhou (não um "falhou" solto): se o endereço da imagem mudar, a nova tem a sua chance.
  const [imagemQueFalhou, setImagemQueFalhou] = useState<string | null>(null);
  useEntradaSemPulo(raizRef);

  const forma = formaDoCartao(previa, forcarCompacta);
  const imagemVisivel = previa.imagem_url !== null && previa.imagem_url !== imagemQueFalhou;
  const secundario = aparencia.startsWith("propria") ? "text-white/90" : "text-foreground/75";
  const imagem = imagemVisivel ? previa.imagem_url : null;

  const comum = {
    ref: raizRef,
    onClick: NAO_SOBE_O_CLIQUE,
    "data-testid": `cartao-de-link-${id}`,
    "data-dominio": previa.dominio,
    "data-forma": forma,
    "data-casa": previa.casa ? "true" : undefined,
    "data-imagem": imagemVisivel ? forma : "nao",
    className: cn(BASE, posicao === "em-cima" ? "mb-1.5" : "mt-2", POR_APARENCIA[aparencia]),
  };

  const conteudo = (
    <>
      {forma === "faixa" && imagem && (
        // A caixa existe ANTES de a imagem carregar: a imagem chega sem segundo pulo.
        <div className="aspect-[1.91/1] w-full bg-foreground/10">
          <img
            src={imagem}
            alt=""
            loading="lazy"
            decoding="async"
            draggable={false}
            referrerPolicy="no-referrer"
            onError={() => setImagemQueFalhou(imagem)}
            className="h-full w-full object-cover"
          />
        </div>
      )}
      <div className="flex gap-2.5 p-2.5">
        {forma === "compacta" && imagem && (
          <img
            src={imagem}
            alt=""
            loading="lazy"
            decoding="async"
            draggable={false}
            referrerPolicy="no-referrer"
            onError={() => setImagemQueFalhou(imagem)}
            className="h-14 w-14 shrink-0 rounded bg-foreground/10 object-cover"
          />
        )}
        {previa.casa && (
          // O logo é transparente e tem marinho: sumiria no índigo da bolha própria. Placa branca.
          <span
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white"
          >
            <img src="/logo.png" alt="" draggable={false} className="h-8 w-8 object-contain" />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="line-clamp-2 font-medium" dir="auto" data-testid="cartao-de-link-titulo">
            {previa.titulo}
          </span>
          {previa.descricao && (
            <span
              dir="auto"
              className={cn(forma === "compacta" ? "block truncate" : "line-clamp-2", secundario)}
              data-testid="cartao-de-link-descricao"
            >
              {previa.descricao}
            </span>
          )}
          <span
            className={cn("flex items-center gap-1", secundario)}
            data-testid="cartao-de-link-dominio"
          >
            <Globe aria-hidden="true" className="h-3 w-3 shrink-0" />
            <span className="truncate">{previa.dominio}</span>
          </span>
          {!previa.casa && <span className="sr-only"> (abre em outra aba)</span>}
        </span>
      </div>
    </>
  );

  if (previa.casa && dentroDoRouter) {
    return (
      <Link to={caminhoRelativoSeguro(previa.caminho ?? "/")} {...comum}>
        {conteudo}
      </Link>
    );
  }
  if (previa.casa) {
    return (
      <a href={previa.url} {...comum}>
        {conteudo}
      </a>
    );
  }
  return (
    <a
      href={previa.url}
      target="_blank"
      rel="noopener noreferrer"
      referrerPolicy="no-referrer"
      {...comum}
    >
      {conteudo}
    </a>
  );
}
